import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import Button from '@/ui-components/Button';
import Input from '@/ui-components/Input';
import ButtonGroup from '@/ui-components/ButtonGroup';
import FieldGroup from '@/ui-components/FieldGroup';
import ErrorsMessage from '@/ui-components/ErrorsMessage';
import Turnstile from '@/ui-components/Turnstile/Turnstile';

import { signup, SignupValuesType, userSelector } from '@/framework/user';

import { setApiErrors } from '@/utils/setApiErrors';

const guestValidationSchema = Yup.object({
  email: Yup.string().email('Not valid email').required('Required email'),
  password: Yup.string().required('Required password'),
});

const signupValidationSchema = Yup.object({
  email: Yup.string().email('Not valid email').required('Required email'),
  username: Yup.string().required('Required username'),
  password: Yup.string().required('Required password'),
});

const SignupForm = () => {
  const navigate = useNavigate();
  const isGuest = useRecoilValue(userSelector.isGuest);
  const [turnstile, setTurnstile] = useState('');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignupValuesType>({
    resolver: yupResolver(
      isGuest ? guestValidationSchema : signupValidationSchema,
    ),
  });

  const onSubmit = async (values: SignupValuesType) => {
    if (process.env.TURNSTILE_SITEKEY && !turnstile) {
      setError('email', { type: 'manual', message: 'Confirm you are not a bot.' });
      return;
    }
    try {
      await signup({ ...values, turnstile });
      navigate('/profile');
    } catch (err) {
      setApiErrors(err.errors, setError);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ErrorsMessage {...{ errors }} />

      <FieldGroup>
        <Input
          type="email"
          placeholder="Email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email')}
        />
        {!isGuest && (
          <Input
            placeholder="Username"
            autoComplete="username"
            error={errors.username?.message}
            {...register('username')}
          />
        )}
        <Input
          type="password"
          autoComplete="new-password"
          placeholder="Password"
          error={errors.password?.message}
          {...register('password')}
        />
      </FieldGroup>

      <Turnstile onToken={setTurnstile} />

      <ButtonGroup>
        <Button size="medium" type="submit" loading={isSubmitting}>
          {isGuest ? 'Save this account' : 'Sign up'}
        </Button>
      </ButtonGroup>
    </form>
  );
};

export default SignupForm;
